import React from 'react';

/**
 * JobCard - Swipeable job card for Daily Match
 *
 * Displays:
 * - Company logo (from Clearbit)
 * - Role title & company name
 * - Match percentage (prominent)
 * - Location & salary
 * - Top highlights
 */

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const now = new Date();
  const posted = new Date(dateStr);
  const diffMs = now - posted;
  if (diffMs < 0 || isNaN(diffMs)) return null;
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return '1 week ago';
  if (weeks < 5) return `${weeks} weeks ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

const JobCard = ({ item, isActive }) => {
  // Extract job data (could be from jobs table or external)
  const jobData = item.externalJobData || {};
  const company = jobData.company || item.company || 'Unknown Company';
  const role = jobData.title || jobData.role || item.role || 'Role';
  const location = jobData.location || 'Location not specified';
  const salary = jobData.salary || null;
  const matchScore = item.preMatchScore || 50;
  const highlights = item.preMatchHighlights || jobData.highlights || [];
  const concerns = item.preMatchConcerns || [];
  const source = item.source || 'unknown';
  const tags = jobData.tags || [];
  const description = jobData.description || '';
  const postedDate = jobData.postedDate || null;
  const postedAgo = timeAgo(postedDate);

  // Get company logo from Clearbit (free)
  const companySlug = company.toLowerCase().replace(/[^a-z0-9]/g, '');
  const logoUrl = `https://logo.clearbit.com/${companySlug}.com`;

  // Match score color
  const getMatchColor = (score) => {
    if (score >= 80) return '#22c55e'; // Green
    if (score >= 60) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  // Match score label
  const getMatchLabel = (score) => {
    if (score >= 85) return 'Excellent';
    if (score >= 75) return 'Strong';
    if (score >= 60) return 'Good';
    return 'Potential';
  };

  // Source badge
  const getSourceBadge = (src) => {
    switch (src) {
      case 'adzuna': return 'Adzuna';
      case 'remoteok': return 'RemoteOK';
      case 'job_radar': return 'Job Radar';
      case 'user_added': return 'Your Job';
      case 'deferred': return 'Saved';
      default: return null;
    }
  };

  const sourceBadge = getSourceBadge(source);

  return (
    <div className={`job-card ${isActive ? 'job-card--active' : ''}`}>
      {/* Top section - company info */}
      <div className="job-card__header">
        <div className="job-card__logo-wrapper">
          <img
            src={logoUrl}
            alt={company}
            className="job-card__logo"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company)}&background=f7c948&color=1a1a1a&size=80`;
            }}
          />
        </div>
        {sourceBadge && (
          <span className={`job-card__source job-card__source--${source}`}>
            {sourceBadge}
          </span>
        )}
      </div>

      {/* Role & company */}
      <div className="job-card__title-section">
        <h2 className="job-card__role">{role}</h2>
        <p className="job-card__company">at {company}</p>
      </div>

      {/* Match score - prominent */}
      <div className="job-card__match">
        <div
          className="job-card__match-ring"
          style={{
            '--match-color': getMatchColor(matchScore),
            '--match-percent': `${matchScore}%`,
          }}
        >
          <svg viewBox="0 0 100 100" className="job-card__match-svg">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#e5e5e5"
              strokeWidth="8"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={getMatchColor(matchScore)}
              strokeWidth="8"
              strokeDasharray={`${matchScore * 2.83} 283`}
              strokeLinecap="round"
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div className="job-card__match-value">
            <span className="job-card__match-percent">{matchScore}%</span>
            <span className="job-card__match-label">{getMatchLabel(matchScore)}</span>
          </div>
        </div>
      </div>

      {/* Location, salary, posted date */}
      <div className="job-card__meta">
        <div className="job-card__meta-item">
          <span className="job-card__meta-icon">📍</span>
          <span>{location}</span>
        </div>
        {salary && (
          <div className="job-card__meta-item">
            <span className="job-card__meta-icon">💰</span>
            <span>{salary}</span>
          </div>
        )}
        {postedAgo && (
          <div className="job-card__meta-item">
            <span className="job-card__meta-icon">🕐</span>
            <span>{postedAgo}</span>
          </div>
        )}
      </div>

      {/* Description preview */}
      {description && (
        <div className="job-card__description">
          {description.length > 150 ? description.slice(0, 150).trim() + '...' : description}
        </div>
      )}

      {/* Highlights */}
      {highlights.length > 0 && (
        <div className="job-card__highlights">
          {highlights.slice(0, 3).map((highlight, i) => (
            <div key={i} className="job-card__highlight">
              <span className="job-card__highlight-icon">✓</span>
              <span>{highlight}</span>
            </div>
          ))}
        </div>
      )}

      {/* Concerns */}
      {concerns.length > 0 && (
        <div className="job-card__concerns">
          {concerns.slice(0, 2).map((concern, i) => (
            <div key={i} className="job-card__concern">
              <span className="job-card__concern-icon">!</span>
              <span>{concern}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tags (from RemoteOK) */}
      {tags.length > 0 && (
        <div className="job-card__tags">
          {tags.slice(0, 4).map((tag, i) => (
            <span key={i} className="job-card__tag">{tag}</span>
          ))}
        </div>
      )}

      {/* Swipe hints */}
      {isActive && (
        <div className="job-card__hints">
          <span className="job-card__hint job-card__hint--left">← Pass</span>
          <span className="job-card__hint job-card__hint--up">↑ Later</span>
          <span className="job-card__hint job-card__hint--right">Apply →</span>
        </div>
      )}
    </div>
  );
};

export default JobCard;
